import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { HomePlanSchemaMove } from "api-types/homePlans/homePlanRequests";
import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import type { ClientLocationPlanBaseData } from "api-types/locationPlans/locationPlansResponses";
import type { DocOptionSet } from "building-types/options/optionSet";
import { formatPricePlusMinus } from "common/utils/formatDollars";
import { docToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import { getOptionChoicesWithDefaults } from "doc/options/optionChoicesTools";
import { optionPrice } from "doc/options/optionSetTools";
import { getBuilderLocationPlanURL, getHomePlanBaseDataURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { optionChoicesNew } from "lib/options/optionChoicesNew";
import { locationPlanLibraryPath } from "lib/routeHelpers";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo } from "react";
import Link from "ui/components/Link";
import Loading from "ui/components/Loading";
import cx from "ui/cx";
import IconExternalLink from "ui/icons/IconExternalLink";
import IconInfoLine from "ui/icons/IconInfoLine";
import IconWarning from "ui/icons/status/IconError";
import Tooltip from "ui/popovers/Tooltip";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import type { OptionChoiceState } from "./MoveHomePlanTypes";
import styles from "./ReviewOptionChanges.module.scss";

type OptionChanges = HomePlanSchemaMove["optionChanges"];

export function ReviewOptionChanges({
  targetLocationPlanId,
  targetLocationId,
  homePlanId,
  builder,
  optionChoiceState,
  setOptionChoiceState,
}: {
  targetLocationPlanId: string;
  targetLocationId: string;
  builder: ClientBuilder;
  homePlanId: string;
  optionChoiceState: OptionChoiceState;
  setOptionChoiceState: Dispatch<SetStateAction<OptionChoiceState>>;
}) {
  // get hydrated home plan for option sets with all overrides applied
  const { data: homePlanData } = useFetch<ClientHomePlanBaseData>(
    getHomePlanBaseDataURL({ builderId: builder.id, homePlanId }),
    [builder.id, homePlanId],
  );

  // get hydrated location plan
  const { data: targetLocationPlan } = useFetch<ClientLocationPlanBaseData>(
    getBuilderLocationPlanURL({
      builderId: builder.id,
      locationId: targetLocationId,
      locationPlanId: targetLocationPlanId,
    }),
    [builder.id, targetLocationId, targetLocationPlanId],
  );

  // initialize optionChoiceState with homeplandata
  useEffect(() => {
    const changes: OptionChanges = {};
    if (!targetLocationPlan) {
      return;
    }
    if (!homePlanData?.homePlan.doc.optionSets) {
      return;
    }

    const locationPlanDoc = {
      ...targetLocationPlan.masterPlan.doc,
      optionSets: targetLocationPlan.optionSets,
      optionEffects: targetLocationPlan.optionEffects,
    };

    // memoize homePlan option sets by option set id
    const sourceOptionSets: Record<string, DocOptionSet> = ({} = Object.entries(
      homePlanData.homePlan.doc.optionSets ?? {},
    ).reduce((acc, [id, set]) => ({ ...acc, [id]: set }), {}));
    const targetOptionSets: Record<string, DocOptionSet> = ({} = Object.entries(
      locationPlanDoc.optionSets ?? {},
    ).reduce((acc, [id, set]) => ({ ...acc, [id]: set }), {}));

    const newOptionChoices = getOptionChoicesWithDefaults(
      docToOptionDefinitions(locationPlanDoc),
      optionChoicesNew(locationPlanDoc, homePlanData.homePlan.optionChoices ?? {}).optionChoices,
    );

    const originalOptionChoices = getOptionChoicesWithDefaults(
      docToOptionDefinitions(homePlanData.homePlan.doc),
      homePlanData.homePlan.optionChoices ?? {},
    );

    for (const [origOptionSetId, origOptionId] of Object.entries(originalOptionChoices)) {
      const newOptionId = newOptionChoices[origOptionSetId];
      const origOption = getOption(sourceOptionSets, origOptionSetId, origOptionId);
      const newOption = getOption(targetOptionSets, origOptionSetId, newOptionId);

      changes[origOptionSetId] = {
        optionSetId: origOptionSetId,
        oldOptionChoice: origOptionId ?? "",
        oldPrice: optionPrice(origOption),
        newOptionChoice: newOptionId ?? "",
        newPrice: optionPrice(newOption),
      };
    }

    setOptionChoiceState({ optionChanges: changes, sourceOptionSets, targetOptionSets });
  }, [homePlanData, targetLocationPlan]);

  const optionChangesColumns: Column<OptionChanges["string"]>[] = useMemo(
    () => [
      {
        header: "Option Set",
        accessor: (c) => optionChoiceState.targetOptionSets[c.optionSetId]?.name ?? "Unknown Option Set",
      },
      {
        header: "Original choice",
        cell: function ChangeCell(c) {
          return (
            <OptionChangeCell
              optionChange={c.row.original}
              targetOptionSet={optionChoiceState.targetOptionSets[c.row.original.optionSetId]}
            />
          );
        },
      },
      {
        header: "New choice",
        cell: function OverrideCell(c) {
          const optionSetId = c.row.original.optionSetId;
          const optionSet = optionChoiceState.targetOptionSets[optionSetId];
          const handleOverride = (newValue: string) => {
            const newOption = getOption(optionChoiceState.targetOptionSets, optionSetId, newValue);

            setOptionChoiceState((prev) => {
              const newOptionChange: OptionChanges["string"] = {
                ...prev.optionChanges[optionSetId],
                newPrice: optionPrice(newOption),
              };

              // if the override value is the same as the new option choice, the override has no effect and we can clear it
              newOptionChange.choiceOverride = newOptionChange.newOptionChoice === newValue ? undefined : newValue;

              return {
                ...prev,
                optionChanges: {
                  ...prev.optionChanges,
                  [optionSetId]: newOptionChange,
                },
              };
            });
          };

          return (
            <SelectOverride
              optionSet={optionSet}
              optionChange={c.row.original}
              onChange={handleOverride}
              disabled={!!homePlanData?.homePlan.lockedAt}
            />
          );
        },
      },
    ],
    [optionChoiceState.optionChanges],
  );

  const rows = useMemo(
    () =>
      Object.values(optionChoiceState.optionChanges).sort((a, b) =>
        optionChoiceState.targetOptionSets[a.optionSetId].name.localeCompare(
          optionChoiceState.targetOptionSets[b.optionSetId].name,
        ),
      ),
    [optionChoiceState.optionChanges],
  );

  const optionPriceChange = useMemo(
    () =>
      Object.values(optionChoiceState.optionChanges).reduce(
        (acc, change) => acc + change.newPrice - change.oldPrice,
        0,
      ),
    [optionChoiceState.optionChanges],
  );

  const basePriceChange =
    (targetLocationPlan?.locationPlan.basePrice ?? 0) - (homePlanData?.communityPlan.basePrice ?? 0);

  return (
    <>
      {!targetLocationPlan ? (
        <Loading />
      ) : (
        <div>
          <p>
            Review changes from moving this Home Plan to{" "}
            <b>
              {targetLocationPlan?.locationPlan.name}{" "}
              <Link
                target="_blank"
                href={locationPlanLibraryPath({
                  builderId: builder.id,
                  locationId: targetLocationPlan.location?.id ?? "",
                  locationPlanId: targetLocationPlan.locationPlan.id,
                })}
              >
                <IconExternalLink size={12} />
              </Link>
            </b>
            . Total price change:{" "}
            <span className={styles.highlightPrice}>{formatPricePlusMinus(basePriceChange + optionPriceChange)}</span>{" "}
            (due to <span className={styles.highlightPrice}>{formatPricePlusMinus(basePriceChange)}</span> in base price
            changes and <span className={styles.highlightPrice}>{formatPricePlusMinus(optionPriceChange)}</span> in
            options).
          </p>
          <div className={styles.tableScroll}>
            <ConfigTable
              data={rows}
              columns={optionChangesColumns}
              emptyCTA="No options will be changed when moving plans."
              fixedHeader
            />
          </div>
        </div>
      )}
    </>
  );
}

function OptionChangeCell({
  optionChange,
  targetOptionSet,
}: {
  optionChange: OptionChanges["string"];
  targetOptionSet: DocOptionSet;
}) {
  const { oldOptionChoice, newOptionChoice, oldPrice, newPrice, choiceOverride } = optionChange;
  const newOptionId = choiceOverride ?? newOptionChoice ?? "";
  const oldOption = targetOptionSet.options[oldOptionChoice ?? ""];
  const newOption = targetOptionSet.options[newOptionId];

  if (!oldOption) {
    console.error("Option not found", { oldOption, newOption, optionChange, targetOptionSet });
    return (
      <Tooltip.Root>
        <Tooltip.Trigger>
          <span>
            <IconWarning />
          </span>
        </Tooltip.Trigger>
        <Tooltip.Content>Option not found</Tooltip.Content>
      </Tooltip.Root>
    );
  }

  const optionChanged = !!newOptionId && newOptionId !== oldOptionChoice;
  const priceDelta = newPrice - oldPrice;

  return (
    <div className={styles.optionChangeCell}>
      {optionChanged && (
        <Tooltip.Root>
          <Tooltip.Trigger>
            <span className={styles.optionChangeCell}>
              <IconInfoLine size={16} />
            </span>
          </Tooltip.Trigger>
          <Tooltip.Content>
            {choiceOverride ? "Option changed by override" : "Option changed by Location Plan"}
          </Tooltip.Content>
        </Tooltip.Root>
      )}
      {oldOptionChoice && <span className={cx({ styles, optionChanged })}>{oldOption.name}</span>}
      {priceDelta !== 0 && <span className={styles.highlightPrice}>{formatPricePlusMinus(priceDelta)}</span>}
    </div>
  );
}

function SelectOverride({
  optionSet,
  optionChange,
  onChange,
  disabled = false,
}: {
  optionSet: DocOptionSet;
  optionChange: OptionChanges["string"];
  onChange: (newValue: string) => void;
  disabled?: boolean;
}) {
  const selectedValue =
    optionChange.choiceOverride ?? optionChange.newOptionChoice ?? optionChange.oldOptionChoice ?? "";

  const active = !!optionChange.choiceOverride;

  return (
    <div className={styles.overrideCell}>
      <select
        onChange={(e) => onChange(e.currentTarget.value)}
        value={selectedValue}
        disabled={disabled}
        title={disabled ? "Home Plan is locked" : ""}
      >
        {Object.entries(optionSet.options).map(([id, option]) => {
          const priceDiff = optionPrice(option) - optionChange.oldPrice;
          return (
            <option key={id} value={id} disabled={option.disabled === true} selected={id === selectedValue}>
              {option.name}{" "}
              {priceDiff !== 0 ? `(${formatPricePlusMinus(optionPrice(option) - optionChange.oldPrice)})` : null}
            </option>
          );
        })}
      </select>
      <button
        onClick={() => onChange(optionChange.newOptionChoice) /** reverts to new option choice on clear */}
        disabled={!active}
        className={cx({ styles, overrideButton: true, active })}
      />
    </div>
  );
}

function getOption(
  optionSets: Record<string, DocOptionSet>,
  optionSetId: string | undefined | null,
  optionId: string | undefined | null,
) {
  return optionSets[optionSetId ?? ""]?.options?.[optionId ?? ""];
}
